'use client';

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Row, Col, Button, Alert, Spinner, Form, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import { useData } from '../../storage/accessors';
import { format, parseISO } from 'date-fns';
import ReflectionDateRangePicker from '../../components/reflection/ReflectionDateRangePicker';
import ReflectionSectionsModal from '../../components/reflection/ReflectionSectionsModal';
import { ForwardRefEditor } from '../../components/markdown/ForwardRefEditor';
import { findWheelSnapshotsForPeriod, extractWheelBalanceData, WheelBalanceData } from '../../components/reflection/wheelSnapshotUtils';
import { findMeSnapshotsForPeriod } from '../../components/me/meSnapshotUtils';
import { calculateAverageRoleEfficiency, getAverageEfficiencyForRole } from '../../components/reflection/roleEfficiencyUtils';
import { MDXEditorMethods } from '@mdxeditor/editor';
import { ReflectionSections } from '../../state/ReflectionSections';
import Icon from '../../components/Icon';
import WheelSnapshotChart from '../../components/reflection/WheelSnapshotChart';
import MeSnapshotComparison from '../../components/me/MeSnapshotComparison';
import LifePartReflectionBlock from '../../components/reflection/LifePartReflectionBlock';
import { extractLifePartPeriodData, LifePartPeriodData } from '../../components/reflection/lifePartSnapshotUtils';
import { faPlus, faTrash, faChartPie } from '@fortawesome/free-solid-svg-icons';
import useActualWheelId from '@/hooks/useActualWheelId';
import { LifePart } from '../../state/LifePart';
import useWheelId from '@/hooks/useWheelId';
import { useReadonly } from '@/contexts/readonlyContext';

interface PreviewData {
    initialWheel: any[];
    finalWheel: any[];
    initialMe: any;
    finalMe: any;
}

interface LifePartPreviewData {
    lifeParts: LifePartPeriodData[];
}

interface RoleEfficiencyCache {
    averageEfficiencies: Map<string, number | null>;
    efficiencySnapshotsCounts: Map<string, number>;
}

export default function ReflectionCreatePage() {
    const navigate = useNavigate();
    const pathWheelId = useWheelId();
    const wheelId = useActualWheelId();
    const { reflections, wheelSnapshots, meSnapshots, conclusions: conclusionsStore, decisions: decisionsStore, roleEfficiencySnapshots } = useData();
    const { readonly } = useReadonly();

    const [dateFrom, setDateFrom] = useState<string>('');
    const [dateTo, setDateTo] = useState<string>('');
    const [title, setTitle] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sectionsModalShow, setSectionsModalShow] = useState(false);
    const [selectedSections, setSelectedSections] = useState<ReflectionSections>({ me: false, balance: false, lifeparts: [] });
    const [lifePartPreviewData, setLifePartPreviewData] = useState<LifePartPreviewData>({ lifeParts: [] });

    const editorRef = useRef<MDXEditorMethods>(null);
    const conclusionsRefs = useRef<(HTMLInputElement | null)[]>([]);
    const decisionsRefs = useRef<(HTMLInputElement | null)[]>([]);
    const [conclusionsCount, setConclusionsCount] = useState<number>(0);
    const [decisionsCount, setDecisionsCount] = useState<number>(0);

    const allWheelSnapshots = Object.values(wheelSnapshots.objects);
    const allMeSnapshots = Object.values(meSnapshots.objects);
    const allRoleEfficiencySnapshots = useMemo(() => 
        Object.values(roleEfficiencySnapshots.objects),
        [roleEfficiencySnapshots.objects]
    );

    // Собираем базовые данные для предпросмотра при изменении дат (не зависит от выбранных сфер)
    const basePreviewData = useMemo(() => {
        if (!dateFrom || !dateTo) {
            return null;
        }

        const fromDate = parseISO(dateFrom);
        const toDate = parseISO(dateTo);

        // Находим снимки колеса на даты начала и окончания
        const { initial: initialWheelSnapshot, final: finalWheelSnapshot } = findWheelSnapshotsForPeriod(
            allWheelSnapshots,
            fromDate,
            toDate
        );

        const initialWheel = extractWheelBalanceData(initialWheelSnapshot);
        const finalWheel = extractWheelBalanceData(finalWheelSnapshot);

        // Находим снимки MeSnapshot
        const { initial: initialMeSnapshot, final: finalMeSnapshot } = findMeSnapshotsForPeriod(
            allMeSnapshots,
            fromDate,
            toDate
        );

        return {
            initialWheel,
            finalWheel,
            initialMe: initialMeSnapshot,
            finalMe: finalMeSnapshot,
            initialWheelSnapshot,
            finalWheelSnapshot,
        };
    }, [dateFrom, dateTo]);

    // Извлекаем данные LifePart только при изменении выбранных сфер (отдельно от базовых данных)
    useEffect(() => {
        if (!basePreviewData || !dateFrom || !dateTo) {
            setLifePartPreviewData({ lifeParts: [] });
            return;
        }

        // Вычисляем среднюю эффективность для каждой роли за период
        const roleNames = new Map<string, string>();
        const roleColors = new Map<string, string>();
        
        // Собираем названия и цвета ролей из начального и конечного снимков
        [...(basePreviewData.initialWheelSnapshot?.lifeParts || []), 
          ...(basePreviewData.finalWheelSnapshot?.lifeParts || [])].forEach(part => {
            roleNames.set(part.uuid, part.name);
            roleColors.set(part.uuid, part.color);
        });

        const efficiencyData = calculateAverageRoleEfficiency(
            allRoleEfficiencySnapshots,
            dateFrom,
            dateTo,
            roleNames,
            roleColors
        );

        // Создаём карты для передачи в extractLifePartPeriodData
        const averageEfficiencies = new Map<string, number | null>();
        const efficiencySnapshotsCounts = new Map<string, number>();
        
        efficiencyData.forEach(data => {
            averageEfficiencies.set(data.uuid, data.averageEfficiency);
            efficiencySnapshotsCounts.set(data.uuid, data.snapshotsCount);
        });

        const lifePartsData = extractLifePartPeriodData(
            basePreviewData.initialWheelSnapshot,
            basePreviewData.finalWheelSnapshot,
            selectedSections.lifeparts,
            averageEfficiencies,
            efficiencySnapshotsCounts,
        );

        setLifePartPreviewData({ lifeParts: lifePartsData });
    }, [selectedSections.lifeparts, basePreviewData, dateFrom, dateTo]);

    // Уникальные LifePart из снимков за период для модалки
    const availableLifeparts = useMemo(() => {
        if (!dateFrom || !dateTo) return [];

        const fromDate = parseISO(dateFrom);
        const toDate = parseISO(dateTo);
        const { initial: initialWheelSnapshot, final: finalWheelSnapshot } = findWheelSnapshotsForPeriod(
            allWheelSnapshots,
            fromDate,
            toDate
        );

        // Собираем все уникальные LifePart из начального и конечного снимков
        const partsMap = new Map<string, LifePart>();

        [...(initialWheelSnapshot?.lifeParts || []), ...(finalWheelSnapshot?.lifeParts || [])].forEach(part => {
            if (!partsMap.has(part.uuid)) {
                partsMap.set(part.uuid, part);
            }
        });

        return Array.from(partsMap.values());
    }, [dateFrom, dateTo, allWheelSnapshots]);

    // Обработчик сохранения секций
    const handleSectionSave = (sectionType: 'me' | 'balance' | 'lifepart' | 'lifepart-all', lifepartUuid?: string) => {
        if (sectionType === 'me') {
            setSelectedSections(prev => ({ ...prev, me: !prev.me }));
        } else if (sectionType === 'balance') {
            setSelectedSections(prev => ({ ...prev, balance: !prev.balance }));
        } else if (sectionType === 'lifepart' && lifepartUuid) {
            setSelectedSections(prev => ({
                ...prev,
                lifeparts: prev.lifeparts.includes(lifepartUuid)
                    ? prev.lifeparts.filter(uuid => uuid !== lifepartUuid)
                    : [...prev.lifeparts, lifepartUuid]
            }));
        } else if (sectionType === 'lifepart-all') {
            const allUuids = availableLifeparts.map(lp => lp.uuid);
            const allSelected = allUuids.every(uuid => selectedSections.lifeparts.includes(uuid));

            setSelectedSections(prev => ({
                ...prev,
                lifeparts: allSelected ? [] : allUuids
            }));
        }
    };

    // Удаление LifePart из выбранных
    const handleRemoveLifepart = (uuid: string) => {
        setSelectedSections(prev => ({
            ...prev,
            lifeparts: prev.lifeparts.filter(u => u !== uuid)
        }));
    };

    const handleSave = async () => {
        if (readonly) return;
        setIsSaving(true);
        setError(null);

        try {
            const thoughtsText = editorRef.current?.getMarkdown() || '';

            const newReflection = {
                uuid: crypto.randomUUID(),
                wheelId: wheelId,
                title: title.trim() || 'Без названия',
                dateFrom,
                dateTo,
                createdAt: Date.now(),
                thoughts: thoughtsText,
                order: Object.keys(reflections.objects).length,
                sections: selectedSections,
            };

            reflections.create(newReflection);

            // Сохраняем выводы
            for (let i = 0; i < conclusionsCount; i++) {
                const text = conclusionsRefs.current[i]?.value || '';
                if (text.trim()) {
                    conclusionsStore.create({
                        uuid: crypto.randomUUID(),
                        wheelId: wheelId,
                        reflectionUuid: newReflection.uuid,
                        text,
                        createdAt: Date.now(),
                        order: i,
                    });
                }
            }

            // Сохраняем решения
            for (let i = 0; i < decisionsCount; i++) {
                const text = decisionsRefs.current[i]?.value || '';
                if (text.trim()) {
                    decisionsStore.create({
                        uuid: crypto.randomUUID(),
                        wheelId: wheelId,
                        reflectionUuid: newReflection.uuid,
                        text,
                        isDone: false,
                        createdAt: Date.now(),
                        order: i,
                    });
                }
            }

            navigate(`/${pathWheelId}/reflection`);
        } catch (err) {
            setError('Не удалось сохранить рефлексию. Попробуйте ещё раз.');
            console.error('Error saving reflection:', err);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="px-3 py-4">
            <Row className="mb-4">
                <Col>
                    <h3>Новая рефлексия</h3>
                    <p className="text-muted">
                        Выберите период и заполните форму
                    </p>
                </Col>
            </Row>

            {error && (
                <Row className="mb-4">
                    <Col>
                        <Alert variant="danger" onClose={() => setError(null)} dismissible>
                            {error}
                        </Alert>
                    </Col>
                </Row>
            )}

            {/* Выбор дат - всегда виден */}
            <Row className="mb-4">
                <Col>
                    <ReflectionDateRangePicker
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        onChange={(from, to) => {
                            setDateFrom(from);
                            setDateTo(to);
                            setTitle(''); // Сбрасываем название при смене периода
                        }}
                    />
                </Col>
            </Row>

            {/* При выбранных датах показываем предпросмотр и форму */}
            {dateFrom && dateTo && (
                <>
                    {/* Название рефлексии */}
                    <Row className="mb-4">
                        <Col>
                            <Form.Group>
                                <Form.Label>Название</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Введите название рефлексии (необязательно)"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                />
                                <Form.Text className="text-muted">
                                    Если не указать, будет использовано "Без названия"
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Кнопка для добавления новых секций - показываем если есть что добавить */}
                    {(!selectedSections.me || !selectedSections.balance || availableLifeparts.length > 0) && (
                        <Row className="mb-4">
                            <Col>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => setSectionsModalShow(true)}
                                >
                                    <Icon icon={faPlus} className="me-1" />
                                    Добавить данные
                                </Button>
                                {selectedSections.lifeparts.length > 0 && (
                                    <span className="ms-2 text-muted small">
                                        Выбрано сфер: {selectedSections.lifeparts.length}
                                    </span>
                                )}
                            </Col>
                        </Row>
                    )}

                    {basePreviewData && selectedSections.me && (
                        <Row className="mb-4">
                            <Col>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h4 className="mb-0">Кто я и моя миссия</h4>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => setSelectedSections(prev => ({ ...prev, me: false }))}
                                    >
                                        <Icon icon={faTrash} /> Удалить блок
                                    </Button>
                                </div>
                                <MeSnapshotComparison
                                    initial={basePreviewData.initialMe || null}
                                    final={basePreviewData.finalMe || null}
                                    dateFrom={dateFrom}
                                    dateTo={dateTo}
                                />
                            </Col>
                        </Row>
                    )}

                    {basePreviewData && selectedSections.balance && (
                        <Row className="mb-4">
                            <Col>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <Icon icon={faChartPie} className="text-secondary" style={{ fontSize: '1.5rem' }} />
                                        <h4 className="mb-0">Баланс</h4>
                                    </div>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => setSelectedSections(prev => ({ ...prev, balance: false }))}
                                    >
                                        <Icon icon={faTrash} /> Удалить блок
                                    </Button>
                                </div>
                                <Row xs={1} md={2} className="g-3 mb-4">
                                            <Col>
                                                <Card className="h-100">
                                                    <Card.Header>
                                                        <strong>
                                                            <Icon icon={faChartPie} className="me-2" />
                                                            Начало периода
                                                        </strong>
                                                        <div className="text-muted small">
                                                            {format(parseISO(dateFrom), 'dd.MM.yyyy')}
                                                        </div>
                                                    </Card.Header>
                                                    <Card.Body className="d-flex align-items-center justify-content-center">
                                                        {!basePreviewData.initialWheel || basePreviewData.initialWheel.length === 0 ? (
                                                            <Alert variant="info" className="mb-0 text-center">
                                                                Нет данных
                                                            </Alert>
                                                        ) : (
                                                            <WheelSnapshotChart
                                                                data={basePreviewData.initialWheel}
                                                            />
                                                        )}
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                            <Col>
                                                <Card className="h-100">
                                                    <Card.Header>
                                                        <strong>
                                                            <Icon icon={faChartPie} className="me-2" />
                                                            Конец периода
                                                        </strong>
                                                        <div className="text-muted small">
                                                            {format(parseISO(dateTo), 'dd.MM.yyyy')}
                                                        </div>
                                                    </Card.Header>
                                                    <Card.Body className="d-flex align-items-center justify-content-center">
                                                        {!basePreviewData.finalWheel || basePreviewData.finalWheel.length === 0 ? (
                                                            <Alert variant="info" className="mb-0 text-center">
                                                                Нет данных
                                                            </Alert>
                                                        ) : (
                                                            <WheelSnapshotChart
                                                                data={basePreviewData.finalWheel}
                                                            />
                                                        )}
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        </Row>
                            </Col>
                        </Row>
                    )}

                    {/* Блоки LifePart */}
                    {selectedSections.lifeparts.length > 0 && lifePartPreviewData.lifeParts.map((lpData) => (
                        <Row key={lpData.uuid} className="mb-4">
                            <Col>
                                <LifePartReflectionBlock
                                    data={lpData}
                                    dateFrom={dateFrom}
                                    dateTo={dateTo}
                                    onRemove={() => handleRemoveLifepart(lpData.uuid)}
                                />
                            </Col>
                        </Row>
                    ))}

                    <ReflectionSectionsModal
                        show={sectionsModalShow}
                        onHide={() => setSectionsModalShow(false)}
                        onSave={handleSectionSave}
                        availableSections={{
                            me: !selectedSections.me,
                            balance: !selectedSections.balance,
                            lifeparts: availableLifeparts || []
                        }}
                        selectedLifeparts={selectedSections.lifeparts || []}
                    />

                    {/* Блок с мыслями, выводами и решениями - всегда показывается при выбранных датах */}
                    {basePreviewData && (
                        <>
                            <Row className="mb-4">
                                <Col>
                                    <h4>Мысли</h4>
                                    <p className="text-muted small mb-3">
                                        Опишите ваши мысли, ощущения, инсайты за этот период
                                    </p>
                                    <ForwardRefEditor
                                        ref={editorRef}
                                        markdown=""
                                        className="lh-1"
                                    />
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <Col>
                                    <h4>Выводы</h4>
                                    <p className="text-muted small mb-3">
                                        Добавьте ключевые выводы, которые вы сделали за этот период
                                    </p>
                                    <div className="mb-3">
                                        {Array.from({ length: conclusionsCount }).map((_, index) => (
                                            <div key={index} className="d-flex gap-2 mb-2">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    ref={el => { conclusionsRefs.current[index] = el; }}
                                                    defaultValue=""
                                                    placeholder="Ваш вывод..."
                                                />
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => {
                                                        conclusionsRefs.current.splice(index, 1);
                                                        setConclusionsCount(conclusionsCount - 1);
                                                    }}
                                                >
                                                    ✕
                                                </Button>
                                            </div>
                                        ))}
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => {
                                                setConclusionsCount(conclusionsCount + 1);
                                                setTimeout(() => {
                                                    conclusionsRefs.current[conclusionsCount]?.focus();
                                                }, 0);
                                            }}
                                        >
                                            + Добавить вывод
                                        </Button>
                                    </div>
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <Col>
                                    <h4>Решения</h4>
                                    <p className="text-muted small mb-3">
                                        Какие решения вы принимаете на будущее?
                                    </p>
                                    <div className="mb-3">
                                        {Array.from({ length: decisionsCount }).map((_, index) => (
                                            <div key={index} className="d-flex gap-2 mb-2">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    ref={el => { decisionsRefs.current[index] = el; }}
                                                    defaultValue=""
                                                    placeholder="Ваше решение..."
                                                />
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => {
                                                        decisionsRefs.current.splice(index, 1);
                                                        setDecisionsCount(decisionsCount - 1);
                                                    }}
                                                >
                                                    ✕
                                                </Button>
                                            </div>
                                        ))}
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => {
                                                setDecisionsCount(decisionsCount + 1);
                                                setTimeout(() => {
                                                    decisionsRefs.current[decisionsCount]?.focus();
                                                }, 0);
                                            }}
                                        >
                                            + Добавить решение
                                        </Button>
                                    </div>
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <Col className="d-flex justify-content-end">
                                    <Button
                                        variant="primary"
                                        size="lg"
                                        onClick={handleSave}
                                        disabled={isSaving || readonly}
                                    >
                                        {isSaving ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Сохранение...
                                            </>
                                        ) : (
                                            'Сохранить рефлексию'
                                        )}
                                    </Button>
                                </Col>
                            </Row>
                        </>
                    )}
                </>
            )}
        </div>
    );
}
